import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "../../data/teacher";
import { userRequest } from "../../utils/requestMethods";
import SkeletonCards from "../common/SkeletonCards";
import {
  FiChevronDown,
  FiChevronUp,
  FiCheckCircle,
  FiXCircle,
  FiMoreVertical,
  FiUsers,
  FiLayers,
} from "react-icons/fi";
import { useClassGroups } from "../../hooks/useClassGroups";
import * as api from "../../utils/classGroupApi";
import { splitArmName } from "../../utils/classGroupHelpers";

const ClassesSection = ({ teacher, school, handleShowUnassignClassModal, refreshKey = 0 }) => {
  const { classGroups, subclassLabel, isEnabled: isGroupingEnabled } = useClassGroups();
  const [classes, setClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [expandedPermissions, setExpandedPermissions] = useState({});
  const [expandedArms, setExpandedArms] = useState({});
  const [openMenuId, setOpenMenuId] = useState(null);
  const [permissionsPopoverId, setPermissionsPopoverId] = useState(null);
  const [roleModalState, setRoleModalState] = useState({
    open: false,
    classData: null,
    role: "",
  });
  const [permissionsModalState, setPermissionsModalState] = useState({
    open: false,
    classData: null,
    permissions: [],
    search: "",
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [actionSuccess, setActionSuccess] = useState("");
  const [actionError, setActionError] = useState("");

  const fetchClasses = async () => {
    setLoadingClasses(true);
    try {
      const { data } = await userRequest.get(
        `/teachers/teacher-assigned-classes/${school?._id}/${teacher._id}`,
      );
      const fetchedClasses = data?.classes ?? data?.assignments ?? [];
      setClasses(Array.isArray(fetchedClasses) ? fetchedClasses : []);
      setExpandedPermissions({}); // Reset permissions on fetch
      setExpandedArms({});
    } catch (error) {
      console.error("Error fetching classes:", error);
      setClasses([]);
    } finally {
      setLoadingClasses(false);
    }
  };

  useEffect(() => {
    if (school?._id && teacher?._id) {
      fetchClasses();
    }
  }, [school?._id, teacher?._id, refreshKey]); // Add refreshKey as dependency

  useEffect(() => {
    const closeMenusOnOutsideClick = (event) => {
      if (!event.target.closest("[data-card-options]")) {
        setOpenMenuId(null);
      }
      if (!event.target.closest("[data-permissions-popover]")) {
        setPermissionsPopoverId(null);
      }
    };

    if (openMenuId !== null || permissionsPopoverId !== null) {
      document.addEventListener("mousedown", closeMenusOnOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", closeMenusOnOutsideClick);
    };
  }, [openMenuId, permissionsPopoverId]);

  const availableRoles = useMemo(
    () => [
      {
        value: "primary",
        label: "Primary",
        description: "Main teacher with full permissions and responsibilities",
      },
      {
        value: "assistant",
        label: "Assistant",
        description: "Teaching assistant with limited permissions",
      },
    ],
    [],
  );

  const availablePermissions = useMemo(
    () => [
      "view_students",
      "view_class_info",
      "view_tests",
      "view_questions",
      "view_results",
      "view_lesson_notes",
      "view_attendance",
      "create_tests",
      "edit_tests",
      "delete_tests",
      "create_questions",
      "edit_questions",
      "delete_questions",
      "create_results",
      "edit_results",
      "publish_results",
      "upload_lesson_notes",
      "edit_lesson_notes",
      "delete_lesson_notes",
      "mark_attendance",
      "edit_attendance",
      "edit_student_info",
      "edit_class_info",
    ],
    [],
  );

  const getRoleBadge = (role) => {
    const roleColors = {
      primary: "bg-blue-100 text-blue-800 border-blue-200",
      assistant: "bg-purple-100 text-purple-800 border-purple-200",
      coordinator: "bg-green-100 text-green-800 border-green-200",
    };

    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
          roleColors[role] || "bg-gray-100 text-gray-800 border-gray-200"
        }`}
      >
        {role?.charAt(0).toUpperCase() + role?.slice(1) || "Teacher"}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    if (status === "active") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <FiCheckCircle className="w-3 h-3 mr-1" />
          Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <FiXCircle className="w-3 h-3 mr-1" />
        Inactive
      </span>
    );
  };

  const formatPermission = (permission) => {
    return permission
      ?.split("_")
      ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      ?.join(" ");
  };

  const togglePermissions = (assignmentId) => {
    setExpandedPermissions((prev) => ({
      ...prev,
      [assignmentId]: !prev[assignmentId],
    }));
  };

  const toggleArms = (groupId) => {
    setExpandedArms((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const toggleMenu = (assignmentId) => {
    setOpenMenuId((prev) => (prev === assignmentId ? null : assignmentId));
  };

  const toErrorString = (payload) => {
    if (!payload) return "An unexpected error occurred.";
    if (typeof payload === "string") return payload;
    if (payload.message) return payload.message;
    if (payload.error) return payload.error;
    try {
      return JSON.stringify(payload);
    } catch {
      return "An unexpected error occurred.";
    }
  };

  const showSuccess = (message) => {
    setActionSuccess(message);
    setTimeout(() => setActionSuccess(""), 4000);
  };

  const showError = (message) => {
    setActionError(message);
    setTimeout(() => setActionError(""), 5000);
  };

  const openRoleModal = (classData, currentRole) => {
    setRoleModalState({
      open: true,
      classData,
      role: currentRole || availableRoles[0]?.value || "",
    });
    setOpenMenuId(null);
  };

  const closeRoleModal = () => {
    setRoleModalState({
      open: false,
      classData: null,
      role: "",
    });
  };

  const openPermissionsModal = (classData, currentPermissions = []) => {
    setPermissionsModalState({
      open: true,
      classData,
      permissions: [...new Set(currentPermissions)],
      search: "",
    });
    setOpenMenuId(null);
  };

  const closePermissionsModal = () => {
    setPermissionsModalState({
      open: false,
      classData: null,
      permissions: [],
      search: "",
    });
  };

  const handleRoleUpdate = async () => {
    if (!roleModalState.classData?._id || !roleModalState.role) return;

    try {
      setActionLoading(true);
      setActionError("");

      await userRequest.patch("/teachers/update-teacher-role-in-class", {
        schoolId: school?._id,
        teacherId: teacher?._id,
        classId: roleModalState.classData._id,
        newRole: roleModalState.role,
      });

      closeRoleModal();
      await fetchClasses();
      showSuccess("Teacher role updated successfully.");
    } catch (error) {
      console.error("Error updating teacher role:", error);
      const payload = error?.response?.data;
      showError(toErrorString(payload));
    } finally {
      setActionLoading(false);
    }
  };

  const handlePermissionsUpdate = async () => {
    if (!permissionsModalState.classData?._id) return;

    try {
      setActionLoading(true);
      setActionError("");

      await userRequest.patch("/teachers/update-teacher-permissions-in-class", {
        schoolId: school?._id,
        teacherId: teacher?._id,
        classId: permissionsModalState.classData._id,
        newPermissions: permissionsModalState.permissions,
      });

      closePermissionsModal();
      await fetchClasses();
      showSuccess("Teacher permissions updated successfully.");
    } catch (error) {
      console.error("Error updating teacher permissions:", error);
      const payload = error?.response?.data;
      showError(toErrorString(payload));
    } finally {
      setActionLoading(false);
    }
  };

  // Group classes into class groups vs standalone classes
  const { groupedAssignments, standaloneAssignments } = useMemo(() => {
    const groupsMap = new Map();
    const standalone = [];

    classes.forEach((assignment, index) => {
      const classData = assignment.class || assignment;
      const groupId = isGroupingEnabled ? api.getClassGroupId(classData.class_group_id) : null;

      if (groupId) {
        if (!groupsMap.has(groupId)) {
          const groupDoc = (classGroups || []).find((g) => String(g._id) === String(groupId));
          groupsMap.set(groupId, {
            groupId,
            groupName:
              groupDoc?.name ||
              splitArmName(classData.class_name)?.base ||
              "Class Group",
            arms: [],
            totalStudents: 0,
          });
        }
        const groupEntry = groupsMap.get(groupId);
        groupEntry.arms.push({
          assignment,
          classData,
          assignmentId: assignment.assignmentId || assignment._id || index,
          role: assignment.teacher_role || assignment.role,
          status: assignment.status,
          permissions: assignment.teacher_permissions || assignment.permissions || [],
        });
        groupEntry.totalStudents += Number(classData.total_students || classData.studentCount || 0);
      } else {
        standalone.push({
          assignment,
          classData,
          assignmentId: assignment.assignmentId || assignment._id || index,
          role: assignment.teacher_role || assignment.role,
          status: assignment.status,
          permissions: assignment.teacher_permissions || assignment.permissions || [],
        });
      }
    });

    return {
      groupedAssignments: Array.from(groupsMap.values()),
      standaloneAssignments: standalone,
    };
  }, [classes, classGroups, isGroupingEnabled]);

  const filteredPermissions = useMemo(() => {
    const search = permissionsModalState.search.trim().toLowerCase();
    if (!search) return availablePermissions;

    return availablePermissions.filter((permission) => permission.toLowerCase().includes(search));
  }, [availablePermissions, permissionsModalState.search]);

  return (
    <div className="flex flex-wrap items-stretch mt-6 mb-6">
      {actionSuccess && (
        <div className="w-full mb-4 text-sm text-green-700 bg-green-100 border border-green-200 px-4 py-2 rounded-lg">
          {actionSuccess}
        </div>
      )}
      {actionError && (
        <div className="w-full mb-4 text-sm text-red-700 bg-red-100 border border-red-200 px-4 py-2 rounded-lg">
          {actionError}
        </div>
      )}
      {loadingClasses ? (
        <SkeletonCards cardCount={3} layout="vertical" width="md:w-1/3 w-1/2" />
      ) : classes.length > 0 ? (
        <>
          {/* Render Grouped Class Cards */}
          {groupedAssignments.map((group) => {
            const representativeArm = group.arms[0];
            const role = representativeArm?.role;
            const status = representativeArm?.status;
            const permissions = Array.from(new Set(group.arms.flatMap((a) => a.permissions)));
            const groupCardKey = `group-${group.groupId}`;

            return (
              <div key={groupCardKey} className="pr-3 md:w-1/3 w-full mb-4">
                <div
                  className="border-2 border-indigo-200 bg-white p-4 rounded-2xl relative shadow-sm hover:shadow-md transition-shadow"
                  data-card-options
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">
                        <FiLayers className="w-5 h-5" />
                      </div>
                      <div>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200 uppercase tracking-wider">
                          Class Group
                        </span>
                        <h4 className="text-base font-bold text-slate-900 mt-0.5">
                          {group.groupName}
                        </h4>
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-slate-600 font-medium mt-1">
                    {group.totalStudents} Total Students • {group.arms.length}{" "}
                    {subclassLabel.toLowerCase()}
                    {group.arms.length !== 1 ? "s" : ""}
                  </p>

                  {/* Role and Status */}
                  <div className="flex items-center gap-2 mt-2 mb-2">
                    {role && getRoleBadge(role)}
                    {status && getStatusBadge(status)}
                  </div>

                  {/* Member Arms Revealing Accordion */}
                  <div className="mt-3 mb-2 pt-2 border-t border-slate-100">
                    <button
                      type="button"
                      onClick={() => toggleArms(group.groupId)}
                      className="flex items-center justify-between w-full text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition-colors py-1.5 px-2 bg-indigo-50/60 hover:bg-indigo-50 rounded-lg"
                    >
                      <span className="flex items-center gap-1.5">
                        <FiUsers className="w-3.5 h-3.5" />
                        Member {subclassLabel}s ({group.arms.length}):
                      </span>
                      {expandedArms[group.groupId] ? (
                        <FiChevronUp className="w-3.5 h-3.5" />
                      ) : (
                        <FiChevronDown className="w-3.5 h-3.5" />
                      )}
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        expandedArms[group.groupId]
                          ? "max-h-96 opacity-100 mt-2"
                          : "max-h-0 opacity-0"
                      }`}
                    >
                      <div className="space-y-1.5 p-2 bg-slate-50 border border-slate-200 rounded-xl text-xs">
                        {group.arms.map((arm) => (
                          <div
                            key={arm.assignmentId}
                            className="flex items-center justify-between py-1.5 px-2.5 bg-white rounded-lg border border-slate-100 shadow-2xs"
                          >
                            <div>
                              <span className="font-semibold text-slate-800">
                                {arm.classData.class_name}
                              </span>
                              {arm.classData.group_label && (
                                <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 font-medium">
                                  {subclassLabel} {arm.classData.group_label}
                                </span>
                              )}
                              <span className="text-slate-400 ml-2 text-[11px]">
                                ({arm.classData.total_students || arm.classData.studentCount || 0}{" "}
                                students)
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  handleShowUnassignClassModal(
                                    arm.classData._id || arm.assignmentId,
                                  )
                                }
                                className="text-[11px] text-rose-500 hover:text-rose-700 font-medium"
                              >
                                Unassign
                              </button>
                              <Link
                                to={`/students/${arm.classData._id}`}
                                className="text-[11px] text-indigo-600 hover:text-indigo-800 font-semibold"
                              >
                                View →
                              </Link>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Permissions Revealing Accordion */}
                  {permissions && permissions.length > 0 && (
                    <div className="mt-2 mb-2 relative" data-permissions-popover>
                      <button
                        type="button"
                        onClick={() => togglePermissions(groupCardKey)}
                        className="flex items-center justify-between w-full text-xs font-medium text-slate-600 hover:text-slate-900 transition-colors"
                      >
                        <span>Permissions ({permissions.length}):</span>
                        {expandedPermissions[groupCardKey] ? (
                          <FiChevronUp className="w-3 h-3" />
                        ) : (
                          <FiChevronDown className="w-3 h-3" />
                        )}
                      </button>
                      <div
                        className={`overflow-hidden transition-all duration-300 ease-in-out ${
                          expandedPermissions[groupCardKey]
                            ? "max-h-24 opacity-100 mt-2"
                            : "max-h-0 opacity-0"
                        }`}
                      >
                        <div className="flex flex-wrap gap-1 relative">
                          {permissions.slice(0, 3).map((permission, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200"
                            >
                              {formatPermission(permission)}
                            </span>
                          ))}
                          {permissions.length > 3 && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setPermissionsPopoverId(
                                  permissionsPopoverId === groupCardKey ? null : groupCardKey,
                                );
                              }}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700 border border-indigo-200 hover:bg-indigo-200 transition-colors"
                            >
                              +{permissions.length - 3} more
                            </button>
                          )}
                        </div>
                      </div>
                      {permissionsPopoverId === groupCardKey && (
                        <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-slate-200 rounded-lg shadow-lg p-3 z-20 max-h-64 overflow-y-auto">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-slate-700">
                              All Permissions ({permissions.length})
                            </span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setPermissionsPopoverId(null);
                              }}
                              className="text-slate-400 hover:text-slate-600 text-xs"
                            >
                              ×
                            </button>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {permissions.map((permission, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200"
                              >
                                {formatPermission(permission)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action links */}
                  <div className="flex items-center justify-between gap-2 mt-4 pt-3 border-t border-slate-100 text-xs">
                    <Link
                      to={`/class-groups/${group.groupId}`}
                      className="text-indigo-600 hover:text-indigo-800 font-semibold"
                    >
                      View Class Group →
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Render Standalone Class Cards */}
          {standaloneAssignments.map((assignment) => {
            const { classData, assignmentId, role, status, permissions } = assignment;

            return (
              <div key={assignmentId} className="pr-3 md:w-1/3 w-full mb-4">
                <div
                  className="border border-[#C5C5E8] bg-white p-4 rounded-2xl relative shadow-xs"
                  data-card-options
                >
                  <button
                    type="button"
                    onClick={() => toggleMenu(assignmentId)}
                    className="absolute top-3 right-3 text-slate-500 hover:text-slate-900 transition-colors bg-white rounded-full p-2"
                    aria-label="Class options"
                  >
                    <FiMoreVertical className="w-5 h-5" />
                  </button>
                  {openMenuId === assignmentId && (
                    <div className="absolute top-10 right-3 bg-white border border-slate-200 rounded-lg shadow-lg w-48 z-10">
                      <button
                        type="button"
                        onClick={() => openRoleModal(classData, role)}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-slate-100 transition-colors"
                      >
                        Edit Teacher Role
                      </button>
                      <button
                        type="button"
                        onClick={() => openPermissionsModal(classData, permissions)}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-slate-100 transition-colors border-t border-slate-100"
                      >
                        Edit Permissions
                      </button>
                    </div>
                  )}
                  <div className="bg-[#EEEEFC] flex items-center justify-center rounded-2xl px-4 py-10 mb-3">
                    <img src={Icons.book} alt="Class Icon" className="w-12 h-12" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {classData.class_name || "Class Name"}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {classData.total_students || classData.studentCount || 0} Students
                    </p>

                    {/* Role and Status */}
                    <div className="flex items-center gap-2 mt-2 mb-2">
                      {role && getRoleBadge(role)}
                      {status && getStatusBadge(status)}
                    </div>

                    {/* Permissions */}
                    {permissions && permissions.length > 0 && (
                      <div className="mt-2 mb-3 relative" data-permissions-popover>
                        <button
                          type="button"
                          onClick={() => togglePermissions(assignmentId)}
                          className="flex items-center justify-between w-full text-xs font-medium text-slate-600 hover:text-slate-900 transition-colors"
                        >
                          <span>Permissions ({permissions.length}):</span>
                          {expandedPermissions[assignmentId] ? (
                            <FiChevronUp className="w-3 h-3" />
                          ) : (
                            <FiChevronDown className="w-3 h-3" />
                          )}
                        </button>
                        <div
                          className={`overflow-hidden transition-all duration-300 ease-in-out ${
                            expandedPermissions[assignmentId]
                              ? "max-h-20 opacity-100 mt-2"
                              : "max-h-0 opacity-0"
                          }`}
                        >
                          <div className="flex flex-wrap gap-1 relative">
                            {permissions.slice(0, 3).map((permission, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200"
                              >
                                {formatPermission(permission)}
                              </span>
                            ))}
                            {permissions.length > 3 && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPermissionsPopoverId(
                                    permissionsPopoverId === assignmentId ? null : assignmentId,
                                  );
                                }}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700 border border-indigo-200 hover:bg-indigo-200 transition-colors"
                              >
                                +{permissions.length - 3} more
                              </button>
                            )}
                          </div>
                        </div>
                        {permissionsPopoverId === assignmentId && (
                          <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-slate-200 rounded-lg shadow-lg p-3 z-20 max-h-64 overflow-y-auto">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-semibold text-slate-700">
                                All Permissions ({permissions.length})
                              </span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPermissionsPopoverId(null);
                                }}
                                className="text-slate-400 hover:text-slate-600 text-xs"
                              >
                                ×
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {permissions.map((permission, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200"
                                >
                                  {formatPermission(permission)}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center justify-between mt-3">
                    <button
                      type="button"
                      onClick={() => handleShowUnassignClassModal(classData._id || assignmentId)}
                      className="sm:w-1/2 w-full bg-[#E26E6A1A] text-[#E26E6A] hover:bg-red-100 font-semibold text-xs sm:px-10 px-2 py-2 rounded-full flex items-center justify-center"
                    >
                      Unassign
                    </button>
                    <div className="sm:pr-3 pr-0 sm:w-1/2 w-full sm:mt-0 mt-2 sm:pl-2 pl-0 flex items-center justify-end">
                      <Link to={`/students/${classData._id || assignmentId}`} className="w-full">
                        <button
                          type="button"
                          className="w-full bg-[#4543B5] hover:bg-indigo-900 text-white font-semibold text-xs sm:px-10 px-2 py-2 rounded-full flex items-center justify-center"
                        >
                          View
                        </button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </>
      ) : (
        <div className="text-center w-full text-gray-500 py-12">
          Teacher is not assigned to any class as a class teacher.
        </div>
      )}

      {/* Edit role modal */}
      {roleModalState.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">Update Teacher Role</h3>
              <button
                type="button"
                onClick={closeRoleModal}
                className="text-slate-500 hover:text-slate-900 transition-colors text-sm"
                disabled={actionLoading}
              >
                Close
              </button>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Choose a new role for{" "}
              <span className="font-medium text-slate-700">
                {roleModalState.classData?.class_name}
              </span>
              .
            </p>
            <div className="space-y-3">
              {availableRoles.map((optionRole) => (
                <label
                  key={optionRole.value}
                  className={`flex items-center justify-between border rounded-lg px-4 py-3 text-sm cursor-pointer transition-colors ${
                    roleModalState.role === optionRole.value
                      ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                      : "border-slate-200 hover:border-indigo-400"
                  }`}
                >
                  <div className="flex flex-col text-left">
                    <span className="font-medium">{optionRole.label}</span>
                    {optionRole.description && (
                      <span className="text-xs text-slate-500 mt-1">{optionRole.description}</span>
                    )}
                  </div>
                  <input
                    type="radio"
                    name="teacherRole"
                    value={optionRole.value}
                    checked={roleModalState.role === optionRole.value}
                    onChange={() =>
                      setRoleModalState((prev) => ({
                        ...prev,
                        role: optionRole.value,
                      }))
                    }
                    className="form-radio text-indigo-600"
                  />
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={closeRoleModal}
                className="px-4 py-2 text-sm rounded-full border border-slate-200 text-slate-600 hover:bg-slate-100 transition-colors"
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRoleUpdate}
                className="px-4 py-2 text-sm rounded-full bg-[#4543B5] text-white hover:bg-indigo-900 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={actionLoading}
              >
                {actionLoading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit permissions modal */}
      {permissionsModalState.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">Update Teacher Permissions</h3>
              <button
                type="button"
                onClick={closePermissionsModal}
                className="text-slate-500 hover:text-slate-900 transition-colors text-sm"
                disabled={actionLoading}
              >
                Close
              </button>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Adjust permissions for{" "}
              <span className="font-medium text-slate-700">
                {permissionsModalState.classData?.class_name}
              </span>
              .
            </p>
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search permissions..."
                value={permissionsModalState.search}
                onChange={(event) =>
                  setPermissionsModalState((prev) => ({
                    ...prev,
                    search: event.target.value,
                  }))
                }
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
              />
            </div>
            <div className="flex-1 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pr-2">
                {filteredPermissions.map((permission) => (
                  <label
                    key={permission}
                    className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2 text-sm hover:border-indigo-400 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={permissionsModalState.permissions.includes(permission)}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setPermissionsModalState((prev) => {
                          const updated = checked
                            ? [...prev.permissions, permission]
                            : prev.permissions.filter((item) => item !== permission);
                          return {
                            ...prev,
                            permissions: updated,
                          };
                        });
                      }}
                      className="form-checkbox text-indigo-600"
                    />
                    <span className="capitalize">{formatPermission(permission)}</span>
                  </label>
                ))}
              </div>
              {filteredPermissions.length === 0 && (
                <div className="text-center text-sm text-slate-500 mt-4">
                  No permissions match your search.
                </div>
              )}
            </div>
            <div className="flex justify-between items-center mt-6">
              <button
                type="button"
                onClick={() =>
                  setPermissionsModalState((prev) => ({
                    ...prev,
                    permissions: availablePermissions,
                  }))
                }
                className="text-sm text-indigo-600 hover:text-indigo-800 transition-colors"
                disabled={actionLoading}
              >
                Select all
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setPermissionsModalState((prev) => ({
                      ...prev,
                      permissions: [],
                    }))
                  }
                  className="px-4 py-2 text-sm rounded-full border border-slate-200 text-slate-600 hover:bg-slate-100 transition-colors"
                  disabled={actionLoading}
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={closePermissionsModal}
                  className="px-4 py-2 text-sm rounded-full border border-slate-200 text-slate-600 hover:bg-slate-100 transition-colors"
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handlePermissionsUpdate}
                  className="px-4 py-2 text-sm rounded-full bg-[#4543B5] text-white hover:bg-indigo-900 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={actionLoading}
                >
                  {actionLoading ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassesSection;
